import type { ReactNode } from 'react';
import { cx } from './cx';

export interface SectionHeaderProps {
  /**
   * The plain part of the heading. Text, or text with a decorative glyph in
   * an aria-hidden span (it shows and wraps with the words, and stays out of
   * the heading's accessible name).
   */
  title: ReactNode;
  /**
   * The word (or two) in orange (--ee-orange-text: the brand orange is too
   * light for text). Optional: a page that keeps its existing title has no
   * accent word to invent, and the heading is then the title alone.
   */
  accent?: string;
  accentPosition?: 'start' | 'end';
  /** Small line above the heading. */
  label?: ReactNode;
  /** One line below the heading. Only real copy; never invented reassurance. */
  tagline?: ReactNode;
  /**
   * A small control (an InfoTip) that flows right after the accent word, on
   * its line when the heading wraps, but outside the heading element so it
   * is not part of the heading's accessible name.
   */
  trailing?: ReactNode;
  as?: 'h1' | 'h2' | 'h3';
  /** page: the screen's main heading. section: a heading inside a page. */
  size?: 'page' | 'section';
  className?: string;
}

/** Two tone heading: one accent word in orange (or none), optional label and tagline. */
export function SectionHeader({
  title,
  accent,
  accentPosition = 'end',
  label,
  tagline,
  trailing,
  as: Heading = 'h1',
  size = 'page',
  className,
}: SectionHeaderProps) {
  const accentNode = accent ? <span className="ee-section-header__accent">{accent}</span> : null;
  return (
    <div
      className={cx(
        'ee-section-header',
        `ee-section-header--${size}`,
        trailing != null && 'ee-section-header--trailing',
        className,
      )}
    >
      {label ? <p className="ee-section-header__label">{label}</p> : null}
      <Heading className="ee-section-header__title">
        {accentNode == null ? (
          title
        ) : accentPosition === 'start' ? (
          <>
            {accentNode} {title}
          </>
        ) : (
          <>
            {title} {accentNode}
          </>
        )}
      </Heading>
      {trailing != null ? <span className="ee-section-header__trailing">{trailing}</span> : null}
      {tagline ? <p className="ee-section-header__tagline">{tagline}</p> : null}
    </div>
  );
}
